// Security page — manage your authenticator (TOTP) factors. Enrollment is also
// forced by MfaGate at sign-in when MFA is enforced; this page lets you view,
// add, or remove factors after the fact.
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/shared/ToastProvider';
import { SessionsCard } from '@/components/auth/SessionsCard';

interface Factor {
  id: string;
  friendly_name?: string | null;
  status: string;
  factor_type: string;
  created_at?: string;
}

export function SecurityPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Change-password card (own state so it never cross-talks with the MFA `msg`).
  const [pwForm, setPwForm] = useState({ newPw: '', confirm: '' });
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  // Enroll flow
  const [enrolling, setEnrolling] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      setFactors(((data?.all ?? []).filter((f) => f.factor_type === 'totp')) as Factor[]);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Failed to load factors' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const verified = factors.filter((f) => f.status === 'verified');
  const mfaOn = verified.length > 0;

  async function startEnroll() {
    setEnrolling(true); setMsg(null); setQr(null); setSecret(null); setPendingId(null); setCode('');
    try {
      const stale = factors.find((f) => f.status === 'unverified');
      if (stale) await supabase.auth.mfa.unenroll({ factorId: stale.id }).catch(() => {});
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      setQr(data.totp.qr_code); setSecret(data.totp.secret); setPendingId(data.id);
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Could not start enrollment' });
      setEnrolling(false);
    }
  }

  async function confirm() {
    if (!pendingId || code.trim().length < 6) return;
    setBusy('verify');
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({ factorId: pendingId });
      if (chErr) throw chErr;
      const { error: vErr } = await supabase.auth.mfa.verify({ factorId: pendingId, challengeId: ch.id, code: code.trim() });
      if (vErr) throw vErr;
      setMsg({ kind: 'ok', text: 'Authenticator enabled' });
      setEnrolling(false); setQr(null); setSecret(null); setPendingId(null); setCode('');
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Invalid code' });
    } finally { setBusy(null); }
  }

  async function remove(id: string) {
    setBusy(id);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
      if (error) throw error;
      setMsg({ kind: 'ok', text: 'Authenticator removed' });
      await load();
    } catch (e) {
      setMsg({ kind: 'err', text: e instanceof Error ? e.message : 'Could not remove' });
    } finally { setBusy(null); }
  }

  // The SPA session is already aal2 (MfaGate steps it up at sign-in), so
  // updateUser({ password }) is accepted without any extra challenge — unlike the
  // ResetPassword recovery flow, which lands at aal1 and must challenge first.
  // 12-char minimum matches ResetPassword.tsx.
  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    if (pwForm.newPw.length < 12) { setPwError('Password must be at least 12 characters.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('The two passwords do not match.'); return; }
    setPwBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
      if (error) throw error;
      toast({ kind: 'success', title: 'Password updated.' });
      setPwForm({ newPw: '', confirm: '' });
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Could not update your password.');
    } finally { setPwBusy(false); }
  }

  const box: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, maxWidth: 640 };
  const pwInput: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
  const pwLabel: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 5px' };
  const btn: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, background: 'var(--accent)', color: 'white', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700 }}>Security</h1>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 20px' }}>
        Protect your account with an authenticator app. A 6-digit code is required after your password on each sign-in.
      </p>

      {msg && (
        <div style={{ maxWidth: 640, marginBottom: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13,
          background: msg.kind === 'ok' ? '#ECFDF5' : '#FEE2E2', color: msg.kind === 'ok' ? '#047857' : '#DC2626' }}>{msg.text}</div>
      )}

      <form onSubmit={changePassword} style={{ ...box, marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Change password</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 16px' }}>
          Use at least 12 characters. You will stay signed in on this device.
        </div>
        {pwError && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, fontSize: 13, background: '#FEE2E2', color: '#DC2626' }}>{pwError}</div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={pwLabel} htmlFor="new-password">New password</label>
          <input id="new-password" type="password" autoComplete="new-password" value={pwForm.newPw}
            onChange={(e) => setPwForm((p) => ({ ...p, newPw: e.target.value }))} style={pwInput} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={pwLabel} htmlFor="confirm-password">Confirm new password</label>
          <input id="confirm-password" type="password" autoComplete="new-password" value={pwForm.confirm}
            onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))} style={pwInput} />
        </div>
        <button type="submit" disabled={pwBusy} style={{ ...btn, opacity: pwBusy ? 0.7 : 1, cursor: pwBusy ? 'not-allowed' : 'pointer' }}>
          {pwBusy ? 'Saving…' : 'Update password'}
        </button>
      </form>

      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Two-factor authentication</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{loading ? 'Checking…' : mfaOn ? 'Enabled' : 'Not enabled'}</div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
            background: mfaOn ? '#ECFDF5' : '#F3F4F6', color: mfaOn ? '#047857' : '#6B7280' }}>{mfaOn ? 'On' : 'Off'}</span>
        </div>

        {verified.map((f) => (
          <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, marginBottom: 8, borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{f.friendly_name || 'Authenticator app'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Verified{f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString()}` : ''}</div>
            </div>
            <button onClick={() => remove(f.id)} disabled={busy !== null}
              style={{ fontSize: 12, fontWeight: 600, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer' }}>
              {busy === f.id ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ))}

        {!enrolling ? (
          <button onClick={startEnroll} disabled={loading} style={{ ...btn, marginTop: 4 }}>
            {mfaOn ? 'Add another authenticator' : 'Enable two-factor authentication'}
          </button>
        ) : (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>1. Scan with your authenticator app</div>
            {/* ps-no-capture: rrweb blockClass. The QR is a data-URI that encodes
                the TOTP shared secret, and the manual key is that same secret in
                text — either one reconstructs the second factor. Fenced on the
                image and the <code> themselves, not the enrolment block, so the
                surrounding steps still replay. */}
            {qr ? <img className="ps-no-capture" src={qr} alt="TOTP QR" width={170} height={170} style={{ background: 'white', padding: 8, border: '1px solid var(--border)', borderRadius: 8 }} /> : <div style={{ color: 'var(--text-secondary)' }}>Generating…</div>}
            {secret && <div style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '8px 0' }}>Manual key: <code className="ps-no-capture" style={{ fontFamily: 'monospace' }}>{secret}</code></div>}
            <div style={{ fontSize: 14, fontWeight: 500, margin: '10px 0 6px' }}>2. Enter the 6-digit code</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="123456"
                style={{ width: 130, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 15, letterSpacing: '0.2em', textAlign: 'center', outline: 'none' }} />
              <button onClick={confirm} disabled={busy === 'verify' || code.trim().length < 6} style={btn}>{busy === 'verify' ? 'Verifying…' : 'Verify & enable'}</button>
              <button onClick={() => setEnrolling(false)} style={{ padding: '9px 12px', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* D-52 (19A §6.5): where this account is signed in, with per-session and
          "all other sessions" sign-out. Self-service, for every signed-in user. */}
      <div style={{ marginTop: 16 }}>
        <SessionsCard />
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12, maxWidth: 640 }}>
        Lost your device? Another administrator (or an operator with server access) can reset your factors.
      </p>
    </div>
  );
}
